// ADDED
/**
 * OffsetOvertimePicker - Wrapper around generic EntityPicker
 * 
 * Provides configuration for selecting Overtime references (Date/Remaining hours)
 * used in Offset Requests.
 */

import React from 'react';
import { EntityPickerField } from '../EntityPicker/EntityPickerField';
import { EntityPickerModal } from '../EntityPicker/EntityPickerModal';

// ── Overtime-specific configuration ──────────────────────────────────────────
const get_ot_config = (employeeId) => ({
  apiEndpoint: `/api/OffsetOvertime/employee/${employeeId}`,
  entityName: "OT Reference",
  idField: "id",
  displayFields: {
    primary: "date",
    secondary: "remainingValue"
  },
  tableHeaders: ["Date", "Remaining"],
  tableAccessors: ["date", "remainingValue"],
  searchFields: [
    { label: "All", field: "all", index: 0 },
    { label: "Date", field: "date", index: 1 },
  ],
  placeholder: "--Select OT Reference--"
});

// ── OffsetOvertimePickerField wrapper ────────────────────────────────────────
/**
 * OffsetOvertimePickerField
 * 
 * Usage:
 * <OffsetOvertimePickerField
 *   label="Reference Date:"
 *   employeeId={formData.employeeId}
 *   referenceId={formData.referenceDate?.id}
 *   displayValue={formData.referenceDate?.date}
 *   onSelect={(otRef) => handleInputChange('referenceDate', otRef)}
 *   onClear={() => handleInputChange('referenceDate', null)}
 *   error={errorState && !formData.referenceDate}
 * />
 */
export function OffsetOvertimePickerField({
  employeeId,
  referenceId, // Maps to entityId
  displayValue = "",
  onSelect,
  onClear,
  disabled,
  ...rest       // label, isRequired, error, etc.
}) {
  // Generate dynamic config based on employeeId
  const config = get_ot_config(employeeId);

  // Format display: just the date part
  const formattedDisplay = displayValue ? displayValue.split('T')[0] : "";

  return (
    <EntityPickerField
      {...rest}
      config={config}
      entityId={referenceId}
      displayValue={formattedDisplay}
      onSelect={onSelect}
      onClear={onClear}
      disabled={disabled || !employeeId} // Lock picker if no employee is selected
    />
  );
}

// ── OffsetOvertimePickerModal wrapper ────────────────────────────────────────
/**
 * OffsetOvertimePickerModal
 * 
 * Standalone modal for OT reference selection
 */
export function OffsetOvertimePickerModal({ isOpen, onClose, setData, employeeId }) {
  const config = get_ot_config(employeeId);
  return (
    <EntityPickerModal
      isOpen={isOpen}
      onClose={onClose}
      setData={setData}
      config={config}
    />
  );
}

export { OffsetOvertimePickerField as default };